// ═══════════════════════════════════════════════════════════════
// LmsCoursePlayer — parcours apprenant : leçons, progression, tuteur IA, éval, certificat
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, CheckCircle, Circle, PlayCircle, FileText, MessageSquare,
         ClipboardCheck, Award, Send, Loader, Download, Paperclip, Music, FileType, Maximize, Minimize,
         Copy, Check, RotateCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './lmsProse.css';
import { lmsApi } from '../../services/lmsApi';
import SlideOverlay from './SlideOverlay';
import { useTheme } from '../../contexts/ThemeContext';

const ACCENT = '#8b5cf6';
const ICONS = { video: PlayCircle, text: FileText, file: FileText, chat_task: MessageSquare,
                assessment: ClipboardCheck, sandbox: FileText };

export default function LmsCoursePlayer({ slug, user, onBack }) {
    const [course, setCourse] = useState(null);
    const [progress, setProgress] = useState({ enrollment: null, map: {} });
    const [active, setActive] = useState(null); // lesson
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        try {
            const { course } = await lmsApi.getCourseBySlug(slug);
            setCourse(course);
            const flat = (course.modules || []).flatMap(m => m.lessons || []);
            if (flat.length) setActive(prev => prev || flat[0]);
            try {
                const p = await lmsApi.getProgress(course.id);
                const map = {};
                (p.progress || []).forEach(r => { map[r.lesson_id] = r.status; });
                setProgress({ enrollment: p.enrollment, map });
            } catch { /* pas inscrit */ }
        } catch (e) { setErr(e.message); }
        finally { setLoading(false); }
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    const markComplete = async (lesson) => {
        try {
            const { enrollment } = await lmsApi.completeLesson(lesson.id);
            setProgress(p => ({ enrollment, map: { ...p.map, [lesson.id]: 'completed' } }));
        } catch (e) { setErr(e.message); }
    };

    if (loading) return <Centered><Loader className="spin" /> Chargement…</Centered>;
    if (err) return <Centered>⚠ {err} <button onClick={onBack} style={linkBtn}>Retour</button></Centered>;
    if (!course) return null;

    const pct = progress.enrollment ? Number(progress.enrollment.progress_pct) : 0;
    const completed = pct >= 100;

    return (
        <div style={{ display: 'flex', height: '100%', gap: 0 }}>
            {/* Sommaire */}
            <div style={{ flex: '0 0 320px', borderRight: '1px solid var(--card-border)', overflowY: 'auto', padding: '16px' }}>
                <button onClick={onBack} style={linkBtn}><ArrowLeft size={14} /> Catalogue</button>
                <h3 style={{ margin: '12px 0 4px', fontSize: 16 }}>{course.title}</h3>
                <ProgressBar pct={pct} />
                {(course.modules || []).map(m => (
                    <div key={m.id} style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{m.title}</div>
                        {(m.lessons || []).map(l => {
                            const Icon = ICONS[l.content_type] || Circle;
                            const done = progress.map[l.id] === 'completed';
                            return (
                                <div key={l.id} onClick={() => setActive(l)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginTop: 4,
                                        background: active?.id === l.id ? 'rgba(139,92,246,0.12)' : 'transparent' }}>
                                    {done ? <CheckCircle size={15} color="#22c55e" /> : <Icon size={15} color={ACCENT} />}
                                    <span style={{ fontSize: 13.5 }}>{l.title}</span>
                                </div>
                            );
                        })}
                    </div>
                ))}
                {completed && (
                    <CertificateButton courseId={course.id} />
                )}
            </div>

            {/* Contenu */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '28px 36px' }}>
                {active ? (
                    <LessonView lesson={active} course={course} user={user}
                        onComplete={() => markComplete(active)}
                        done={progress.map[active.id] === 'completed'} />
                ) : <div style={{ color: 'var(--text-secondary)' }}>Sélectionne une leçon.</div>}
            </div>
        </div>
    );
}

// Aperçu d'une fiche HTML autonome (mémo « carnet de terrain ») rendue dans une
// iframe sandboxée (pas de script ; same-origin pour l'auto-hauteur). Bouton plein écran.
function HtmlLesson({ html, title }) {
    const ref = useRef(null);
    const wrapRef = useRef(null);
    const [h, setH] = useState(1200);
    const fit = () => {
        try {
            const d = ref.current?.contentDocument;
            const ht = d && Math.max(d.body?.scrollHeight || 0, d.documentElement?.scrollHeight || 0);
            if (ht) setH(ht + 24);
            // re-mesure après chargement des polices (reflow)
            if (d?.fonts?.ready) d.fonts.ready.then(() => { try { const n = Math.max(d.body.scrollHeight, d.documentElement.scrollHeight); if (n) setH(n + 24); } catch (e) {} });
        } catch (e) { /* sandbox cross-origin : on garde la hauteur par défaut */ }
    };
    const toggleFs = () => {
        const el = wrapRef.current; if (!el) return;
        if (document.fullscreenElement || document.webkitFullscreenElement) (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
        else (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    };
    return (
        <div ref={wrapRef} style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--card-border)', background: '#F4EFE5' }}>
            <iframe ref={ref} srcDoc={html} title={title} onLoad={fit}
                sandbox="allow-same-origin allow-popups"
                style={{ width: '100%', height: h, border: 0, display: 'block' }} />
            <button onClick={toggleFs} title="Plein écran" aria-label="Plein écran"
                style={{ position: 'absolute', top: 10, right: 10, width: 34, height: 34, borderRadius: 8,
                    border: '1px solid rgba(0,0,0,.12)', background: 'rgba(255,255,255,.82)', cursor: 'pointer',
                    display: 'grid', placeItems: 'center', backdropFilter: 'blur(4px)' }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#0A6A60" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H3v5M16 3h5v5M21 16v5h-5M3 16v5h5" /></svg>
            </button>
        </div>
    );
}

function LessonView({ lesson, course, user, onComplete, done }) {
    return (
        <div>
            <h2 style={{ marginTop: 0 }}>{lesson.title}</h2>
            {lesson.content_type === 'text' && (
                /^\s*<(?:!doctype\s+html|html[\s>])/i.test(lesson.body_markdown || '')
                    ? <HtmlLesson html={lesson.body_markdown} title={lesson.title} />
                    : <div className="lms-prose"><ReactMarkdown remarkPlugins={[remarkGfm]}>{lesson.body_markdown || ''}</ReactMarkdown></div>
            )}
            {lesson.content_type === 'video' && <VideoLesson lesson={lesson} />}
            {lesson.content_type === 'file' && <FileLesson lesson={lesson} />}
            {lesson.content_type === 'chat_task' && (
                <TutorChat lesson={lesson} projectId={course.project_id} />
            )}
            {lesson.content_type === 'assessment' && lesson.assessment_id && (
                <AssessmentRunner assessmentId={lesson.assessment_id} />
            )}
            <ResourcesBlock lesson={lesson} />
            {lesson.content_type !== 'assessment' && (
                <button onClick={onComplete} disabled={done}
                    style={{ ...primaryBtn, marginTop: 24, opacity: done ? 0.5 : 1 }}>
                    {done ? '✓ Terminée' : 'Marquer comme terminée'}
                </button>
            )}
        </div>
    );
}

// Convertit une URL YouTube/Vimeo en URL d'embed iframe ; null si ce n'est pas de l'embed.
function toEmbedSrc(url) {
    if (!url || typeof url !== 'string') return null;
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
    return null;
}

// Leçon vidéo : résout l'URL de lecture côté serveur (signée H2 pour l'auto-hébergé,
// URL externe sinon), puis rend un <iframe> (YouTube/Vimeo) ou un <video> (MP4/byte-range).
function VideoLesson({ lesson }) {
    const [url, setUrl] = useState(null);
    const [err, setErr] = useState(null);
    const [titleOn, setTitleOn] = useState(true); // calque titre : visible au départ
    const [paused, setPaused] = useState(true);
    const [hover, setHover] = useState(false);
    const [fs, setFs] = useState(false);
    const [cur, setCur] = useState(0); // position de lecture (s) → calque moments + chapitre actif
    const hideRef = useRef(null);
    const boxRef = useRef(null);
    const vidRef = useRef(null);
    const { isDark } = useTheme();

    // Moments-clés & chapitres (calque soft synchronisé) — injectés dans l'objet leçon.
    const moments = Array.isArray(lesson.key_moments) ? lesson.key_moments : [];
    const chapters = (Array.isArray(lesson.chapters) ? lesson.chapters : [])
        .slice().sort((a, b) => (a.start || 0) - (b.start || 0));
    const activeMoment = moments.find(m => cur >= (m.start || 0) && cur <= (m.end ?? Infinity));
    const activeChapIdx = chapters.reduce((acc, c, i) => (cur >= (c.start || 0) ? i : acc), -1);
    const seekTo = (t) => { const v = vidRef.current; if (v) { v.currentTime = t; if (v.paused) v.play?.().catch(() => {}); } };
    const fmtTime = (t) => { const s = Math.max(0, Math.floor(t || 0)); const m = Math.floor(s / 60); return `${m}:${String(s % 60).padStart(2, '0')}`; };

    useEffect(() => {
        let alive = true;
        setUrl(null); setErr(null);
        if (!lesson.video_url) { setErr('Vidéo non configurée.'); return; }
        lmsApi.getLessonVideoUrl(lesson.id)
            .then(r => { if (alive) setUrl(r.url); })
            .catch(e => { if (alive) setErr(e.message || 'Vidéo indisponible.'); });
        return () => { alive = false; };
    }, [lesson.id, lesson.video_url]);

    useEffect(() => () => clearTimeout(hideRef.current), []); // nettoyage du timer au démontage

    // Plein écran : on suit l'état pour adapter le rendu ; à l'entrée on ré-affiche le titre.
    useEffect(() => {
        const onFs = () => { setFs(!!(document.fullscreenElement || document.webkitFullscreenElement)); clearTimeout(hideRef.current); setTitleOn(true); };
        document.addEventListener('fullscreenchange', onFs);
        document.addEventListener('webkitfullscreenchange', onFs);
        return () => { document.removeEventListener('fullscreenchange', onFs); document.removeEventListener('webkitfullscreenchange', onFs); };
    }, []);

    // Apparaît puis s'estompe (~4 s) ; reste affiché sur pause / survol.
    const showThenFade = () => { clearTimeout(hideRef.current); setTitleOn(true); hideRef.current = setTimeout(() => setTitleOn(false), 4000); };
    const keepShown = () => { clearTimeout(hideRef.current); setTitleOn(true); };
    // Plein écran sur le CONTENEUR (vidéo + calque) → le titre survit au plein écran.
    const toggleFs = () => {
        const el = boxRef.current; if (!el) return;
        if (document.fullscreenElement || document.webkitFullscreenElement) {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
        } else {
            (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
        }
    };

    if (err) return <div style={{ color: 'var(--text-secondary)' }}>⚠ {err}</div>;
    if (!url) return <div style={{ color: 'var(--text-secondary)' }}><Loader size={14} className="spin" /> Chargement de la vidéo…</div>;

    const embed = toEmbedSrc(url);
    if (embed) {
        return (
            <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 10, overflow: 'hidden', background: '#000' }}>
                <iframe src={embed} title={lesson.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} />
            </div>
        );
    }
    // ── Mode SLIDE (calques animés synchronisés, migration 123) : vidéo en MÉDAILLON + SlideOverlay plein cadre.
    //    Activé par lesson.layout === 'slide'. Sinon : comportement legacy (calque 46%) inchangé ci-dessous.
    if (lesson.layout === 'slide') {
        const overlays = Array.isArray(lesson.overlays) ? lesson.overlays : [];
        const activeOverlay = overlays.find(o => cur >= (o.start || 0) && cur <= (o.end ?? Infinity)) || null;
        const pres = lesson.presenter || null;
        const seq = pres?.seq || { index: (lesson.position ?? 0) + 1, total: pres?.total || 6 };
        // Le <video> est ACCUEILLI par SlideOverlay dans son slot médaillon (il le stylise en object-fit:cover).
        const videoEl = (
            <video ref={vidRef} src={url} controls controlsList="nodownload nofullscreen"
                onPlay={() => { setPaused(false); showThenFade(); }}
                onPause={() => { setPaused(true); keepShown(); }}
                onTimeUpdate={(e) => setCur(e.target.currentTime)} />
        );
        return (
            <>
            <div ref={boxRef} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: fs ? 0 : 10,
                    overflow: 'hidden', background: '#000' }}>
                <SlideOverlay overlay={activeOverlay} presenter={pres} isDark={isDark} scale="full"
                    formationLabel={pres?.formation || lesson.title} seq={seq} medallion={videoEl} />
                <button onClick={toggleFs} title={fs ? 'Quitter le plein écran' : 'Plein écran'}
                    style={{ position: 'absolute', top: 10, right: 10, zIndex: 8, pointerEvents: 'auto',
                        opacity: hover || fs ? 1 : 0, transition: 'opacity .25s ease',
                        background: 'rgba(0,0,0,.5)', border: 'none', borderRadius: 8, padding: 6, color: '#fff',
                        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {fs ? <Minimize size={16} /> : <Maximize size={16} />}
                </button>
            </div>
            {chapters.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {chapters.map((c, i) => {
                        const on = i === activeChapIdx;
                        return (
                            <button key={i} onClick={() => seekTo(c.start || 0)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 999,
                                    border: `1px solid ${on ? ACCENT : 'var(--card-border)'}`,
                                    background: on ? 'rgba(139,92,246,0.14)' : 'var(--card-bg)',
                                    color: on ? ACCENT : 'var(--text)', cursor: 'pointer', fontSize: 12.5, fontWeight: on ? 700 : 500,
                                    transition: 'background .2s, border-color .2s' }}>
                                <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75, fontSize: 11.5 }}>{fmtTime(c.start)}</span>
                                {c.title}
                            </button>
                        );
                    })}
                </div>
            )}
            </>
        );
    }

    // Le calque affiche l'accroche du moment actif (synchronisée) ; sinon le titre de la leçon.
    const overlayText = activeMoment ? activeMoment.text : lesson.title;
    const overlayVisible = activeMoment ? true : titleOn;
    return (
        <>
        <div ref={boxRef}
            onMouseEnter={() => { setHover(true); keepShown(); }}
            onMouseLeave={() => { setHover(false); if (!paused) showThenFade(); }}
            style={{ position: 'relative', borderRadius: fs ? 0 : 10, overflow: 'hidden', background: '#000',
                display: fs ? 'flex' : 'block', alignItems: 'center', justifyContent: 'center' }}>
            <video ref={vidRef} src={url} controls controlsList="nodownload nofullscreen"
                onPlay={() => { setPaused(false); showThenFade(); }}
                onPause={() => { setPaused(true); keepShown(); }}
                onTimeUpdate={(e) => setCur(e.target.currentTime)}
                style={{ display: 'block', width: '100%', maxHeight: fs ? '100vh' : 'none', background: '#000' }} />

            {/* Calque soft — zone marbrée à droite du téléphone : accroche du moment actif, sinon titre */}
            <div aria-hidden style={{ position: 'absolute', top: 0, right: 0, width: '46%', height: '100%',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start',
                padding: '0 4% 7% 1%', pointerEvents: 'none', boxSizing: 'border-box',
                opacity: overlayVisible ? 1 : 0, transition: 'opacity .7s ease' }}>
                <div key={activeMoment ? activeMoment.id || activeMoment.start : 'title'}
                    style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: '#d8b878', fontWeight: 700,
                    fontSize: activeMoment ? 'clamp(13px, 2.1vw, 27px)' : 'clamp(13px, 2.3vw, 30px)',
                    lineHeight: 1.18, textTransform: 'uppercase', letterSpacing: '.04em',
                    textShadow: '0 2px 14px rgba(0,0,0,.75)', animation: 'lmsMomentIn .55s ease' }}>
                    {overlayText}
                </div>
            </div>

            {/* Bouton plein écran custom (fullscreen le conteneur → le calque reste visible) */}
            <button onClick={toggleFs} title={fs ? 'Quitter le plein écran' : 'Plein écran'}
                style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, pointerEvents: 'auto',
                    opacity: hover || fs ? 1 : 0, transition: 'opacity .25s ease',
                    background: 'rgba(0,0,0,.5)', border: 'none', borderRadius: 8, padding: 6, color: '#fff', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {fs ? <Minimize size={16} /> : <Maximize size={16} />}
            </button>
        </div>

        {/* Barre de chapitres horodatés — clic = saut dans la vidéo (masquée si aucun chapitre) */}
        {chapters.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {chapters.map((c, i) => {
                    const on = i === activeChapIdx;
                    return (
                        <button key={i} onClick={() => seekTo(c.start || 0)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 11px', borderRadius: 999,
                                border: `1px solid ${on ? ACCENT : 'var(--card-border)'}`,
                                background: on ? 'rgba(139,92,246,0.14)' : 'var(--card-bg)',
                                color: on ? ACCENT : 'var(--text)', cursor: 'pointer', fontSize: 12.5, fontWeight: on ? 700 : 500,
                                transition: 'background .2s, border-color .2s' }}>
                            <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.75, fontSize: 11.5 }}>{fmtTime(c.start)}</span>
                            {c.title}
                        </button>
                    );
                })}
            </div>
        )}
        </>
    );
}

// Leçon fichier : ouvre le support via l'endpoint authentifié (blob) ou la redirection file_ref.
function FileLesson({ lesson }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    if (!lesson.file_id && !lesson.file_ref) {
        return <div style={{ color: 'var(--text-secondary)' }}>Aucun support attaché à cette leçon.</div>;
    }

    const open = async () => {
        setBusy(true); setErr(null);
        const win = window.open('', '_blank'); // ouvert dans le geste utilisateur (anti popup-blocker)
        try {
            const u = await lmsApi.fetchLessonFileBlobUrl(lesson.id);
            if (win) win.location = u; else window.location.assign(u);
        } catch (e) {
            if (win) win.close();
            setErr(e.message || 'Ouverture impossible.');
        } finally { setBusy(false); }
    };

    return (
        <div>
            <button onClick={open} disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}>
                {busy ? <Loader size={14} className="spin" /> : <FileText size={14} />} Ouvrir le support
            </button>
            {err && <div style={{ marginTop: 10, color: '#ef4444' }}>⚠ {err}</div>}
        </div>
    );
}

// Ressources téléchargeables d'une leçon (transcript, slides, document, audio…) — bloc premium.
const RESOURCE_KINDS = {
    transcript: { label: 'Transcription', Icon: FileText },
    slides:     { label: 'Slides',        Icon: FileType },
    document:   { label: 'Document',      Icon: FileText },
    audio:      { label: 'Audio',         Icon: Music },
    other:      { label: 'Ressource',     Icon: Paperclip },
};
function ResourcesBlock({ lesson }) {
    const items = Array.isArray(lesson.resources) ? lesson.resources : [];
    const [busyId, setBusyId] = useState(null);
    const [err, setErr] = useState(null);
    if (!items.length) return null;

    const open = async (r) => {
        setBusyId(r.id); setErr(null);
        const win = window.open('', '_blank'); // dans le geste utilisateur (anti popup-blocker)
        try {
            const u = await lmsApi.fetchResourceBlobUrl(lesson.id, r.id);
            if (win) win.location = u; else window.location.assign(u);
        } catch (e) {
            if (win) win.close();
            setErr(e.message || 'Téléchargement impossible.');
        } finally { setBusyId(null); }
    };

    return (
        <div style={{ marginTop: 24, border: '1px solid var(--card-border)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: 'rgba(139,92,246,0.08)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Paperclip size={14} color={ACCENT} /> Ressources à télécharger
            </div>
            <div style={{ padding: 8 }}>
                {items.map((r) => {
                    const meta = RESOURCE_KINDS[r.kind] || RESOURCE_KINDS.other;
                    const Icon = meta.Icon;
                    return (
                        <button key={r.id} onClick={() => open(r)} disabled={busyId === r.id}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                                padding: '10px 12px', borderRadius: 8, border: '1px solid var(--card-border)',
                                background: 'var(--card-bg)', color: 'var(--text)', cursor: 'pointer', marginTop: 6 }}>
                            <span style={{ display: 'inline-flex', width: 34, height: 34, borderRadius: 8, alignItems: 'center', justifyContent: 'center', background: 'rgba(139,92,246,0.12)', color: ACCENT, flexShrink: 0 }}>
                                <Icon size={17} />
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{r.label || meta.label}</span>
                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{meta.label}</span>
                            </span>
                            {busyId === r.id ? <Loader size={16} className="spin" /> : <Download size={16} color={ACCENT} />}
                        </button>
                    );
                })}
                {err && <div style={{ marginTop: 8, color: '#ef4444', fontSize: 13 }}>⚠ {err}</div>}
            </div>
        </div>
    );
}

// Normalisation d'une question pour le cache anti-doublon.
// RÈGLE PARTAGÉE avec le backend (LmsManager.normalizeTutorQuestion).
function normalizeQuestion(s) {
    return String(s || '')
        .toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

// Déclenche le téléchargement d'un contenu texte en fichier.
function downloadText(filename, text, mime = 'text/markdown;charset=utf-8') {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const slugify = (s) => String(s || 'lecon').toLowerCase().normalize('NFD')
    .replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'lecon';

// Tuteur IA : réutilise le chat du projet backing (SSE) + chat_prompt en addendum système.
// Historique persistant (recharge sans reconsommer de tokens) + cache anti-doublon.
function TutorChat({ lesson, projectId }) {
    const [msgs, setMsgs] = useState([]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [loadingHist, setLoadingHist] = useState(true);
    const [copiedIdx, setCopiedIdx] = useState(null);
    const boxRef = useRef(null);
    const cacheRef = useRef(new Map()); // question_norm → réponse (cache 0-token)

    // Hydratation de l'historique au montage / changement de leçon.
    useEffect(() => {
        let alive = true;
        setLoadingHist(true); setMsgs([]); cacheRef.current = new Map();
        lmsApi.getTutorMessages(lesson.id)
            .then(({ messages }) => {
                if (!alive) return;
                const hydrated = [];
                (messages || []).forEach(r => {
                    hydrated.push({ role: 'user', content: r.question });
                    hydrated.push({ role: 'assistant', content: r.answer, question: r.question });
                    cacheRef.current.set(normalizeQuestion(r.question), r.answer);
                });
                setMsgs(hydrated);
                requestAnimationFrame(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; });
            })
            .catch(() => { /* pas inscrit / pas d'historique : panneau vide */ })
            .finally(() => { if (alive) setLoadingHist(false); });
        return () => { alive = false; };
    }, [lesson.id]);

    const scrollBottom = () => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; };

    const send = async (forcedText, { force = false } = {}) => {
        const fromInput = forcedText == null;
        const text = (fromInput ? input : forcedText).trim();
        if (!text || busy) return;
        if (fromInput) setInput('');
        const norm = normalizeQuestion(text);

        // Anti-doublon : réponse déjà connue → réutilisation instantanée, 0 token.
        if (!force && cacheRef.current.has(norm)) {
            setMsgs(m => [...m,
                { role: 'user', content: text },
                { role: 'assistant', content: cacheRef.current.get(norm), question: text, fromCache: true }]);
            requestAnimationFrame(scrollBottom);
            return;
        }

        setBusy(true);
        setMsgs(m => [...m, { role: 'user', content: text }, { role: 'assistant', content: '', question: text }]);
        let answer = '';
        let meta = { model: null, cost_usd: null };
        try {
            const res = await fetch(`/api/projects/${projectId}/chat/stream`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${localStorage.getItem('oracle_token')}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: text, lms_lesson_id: lesson.id, initial_system_addendum: lesson.chat_prompt || '' }),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => ({ error: `Erreur ${res.status}` }));
                throw new Error(e.error || `Erreur ${res.status}`);
            }
            const reader = res.body.getReader();
            const dec = new TextDecoder();
            let buf = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += dec.decode(value, { stream: true });
                const parts = buf.split('\n\n'); buf = parts.pop();
                for (const p of parts) {
                    const line = p.split('\n').find(l => l.startsWith('data:'));
                    if (!line) continue;
                    try {
                        const ev = JSON.parse(line.slice(5).trim());
                        if (ev.type === 'content' || ev.content) {
                            answer += (ev.content || ev.delta || '');
                            setMsgs(m => { const c = [...m]; c[c.length - 1].content = answer; return c; });
                        }
                        if (ev.type === 'done' || ev.cost) {
                            meta.model = ev.model || ev.meta?.model || meta.model;
                            meta.cost_usd = ev.cost?.cost_usd ?? ev.cost_usd ?? meta.cost_usd;
                        }
                    } catch { /* status events */ }
                }
                scrollBottom();
            }
            // Persistance + mise en cache (fire-and-forget) si réponse réelle.
            if (answer.trim()) {
                cacheRef.current.set(norm, answer);
                lmsApi.saveTutorMessage(lesson.id, {
                    question: text, answer, model: meta.model, cost_usd: meta.cost_usd,
                }).catch(() => { /* persistance best-effort */ });
            }
        } catch (e) {
            setMsgs(m => { const c = [...m]; c[c.length - 1].content = '⚠ ' + e.message; return c; });
        } finally { setBusy(false); }
    };

    const copyAnswer = (idx, content) => {
        navigator.clipboard?.writeText(content).then(() => {
            setCopiedIdx(idx);
            setTimeout(() => setCopiedIdx(c => (c === idx ? null : c)), 1500);
        }).catch(() => {});
    };

    const downloadAnswer = (m) => {
        const md = `# ${lesson.title}\n\n**Question :** ${m.question || ''}\n\n---\n\n${m.content}\n`;
        downloadText(`tuteur-${slugify(lesson.title)}-reponse.md`, md);
    };

    const downloadConversation = () => {
        let md = `# Tuteur IA — ${lesson.title}\n\n`;
        for (let i = 0; i < msgs.length; i++) {
            const m = msgs[i];
            if (m.role === 'user') md += `## ❓ ${m.content}\n\n`;
            else md += `${m.content}\n\n---\n\n`;
        }
        downloadText(`tuteur-${slugify(lesson.title)}-conversation.md`, md);
    };

    return (
        <div style={{ border: '1px solid var(--card-border)', borderRadius: 12, overflow: 'hidden', marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                padding: '10px 14px', background: 'rgba(139,92,246,0.08)', fontSize: 13, fontWeight: 600 }}>
                <span><MessageSquare size={14} style={{ verticalAlign: -2 }} /> Tuteur IA — nourri par les bases du cours</span>
                {msgs.length > 0 && (
                    <button onClick={downloadConversation} title="Télécharger la conversation (.md)"
                        style={iconBtn}><Download size={14} /> <span style={{ fontSize: 12 }}>.md</span></button>
                )}
            </div>
            <div ref={boxRef} style={{ maxHeight: 420, overflowY: 'auto', padding: 16 }}>
                {loadingHist && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}><Loader size={14} className="spin" style={{ verticalAlign: -2 }} /> Chargement de l'historique…</div>}
                {!loadingHist && msgs.length === 0 && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{lesson.chat_prompt || 'Pose ta question au tuteur.'}</div>}
                {msgs.map((m, i) => m.role === 'user' ? (
                    <div key={i} style={{ textAlign: 'right', margin: '16px 0 6px' }}>
                        <div style={{ display: 'inline-block', padding: '8px 12px', borderRadius: 12, maxWidth: '85%',
                            background: ACCENT, color: '#fff', fontSize: 14, textAlign: 'left' }}>{m.content}</div>
                    </div>
                ) : (
                    <div key={i} style={{ margin: '0 0 8px' }}>
                        <div style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                            <div className="lms-prose lms-tutor-answer">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || '…'}</ReactMarkdown>
                            </div>
                            {m.content && !m.content.startsWith('⚠') && (
                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 10,
                                    paddingTop: 10, borderTop: '1px solid var(--card-border)' }}>
                                    {m.fromCache && (
                                        <span style={{ fontSize: 11.5, fontWeight: 600, color: '#22c55e',
                                            background: 'rgba(34,197,94,0.12)', padding: '2px 8px', borderRadius: 20 }}>
                                            ✓ déjà répondu — 0 token
                                        </span>
                                    )}
                                    <button onClick={() => copyAnswer(i, m.content)} style={iconBtn} title="Copier la réponse">
                                        {copiedIdx === i ? <Check size={13} color="#22c55e" /> : <Copy size={13} />}
                                        <span style={{ fontSize: 12 }}>{copiedIdx === i ? 'Copié' : 'Copier'}</span>
                                    </button>
                                    <button onClick={() => downloadAnswer(m)} style={iconBtn} title="Télécharger en .md">
                                        <Download size={13} /> <span style={{ fontSize: 12 }}>.md</span>
                                    </button>
                                    {m.fromCache && (
                                        <button onClick={() => send(m.question, { force: true })} disabled={busy} style={iconBtn}
                                            title="Forcer un nouvel appel au tuteur">
                                            <RotateCcw size={13} /> <span style={{ fontSize: 12 }}>Redemander quand même</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid var(--card-border)' }}>
                <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
                    placeholder="Ta question au tuteur…" style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }} />
                <button onClick={() => send()} disabled={busy} style={primaryBtn}>{busy ? <Loader size={15} className="spin" /> : <Send size={15} />}</button>
            </div>
        </div>
    );
}

function AssessmentRunner({ assessmentId }) {
    const [a, setA] = useState(null);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { lmsApi.getAssessment(assessmentId).then(r => setA(r.assessment)).catch(() => {}); }, [assessmentId]);
    if (!a) return <div style={{ color: 'var(--text-secondary)' }}>Chargement de l'évaluation…</div>;

    const submit = async () => {
        setBusy(true);
        try { const { attempt } = await lmsApi.attempt(a.id, answers); setResult(attempt); }
        catch (e) { setResult({ error: e.message }); }
        finally { setBusy(false); }
    };

    return (
        <div>
            <h3>{a.title}</h3>
            {(a.questions || []).map((q, i) => (
                <div key={q.id || i} style={{ margin: '14px 0' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{i + 1}. {q.text}</div>
                    {(q.type || 'open') === 'open' ? (
                        <textarea value={answers[q.id] || ''} onChange={e => setAnswers(s => ({ ...s, [q.id]: e.target.value }))}
                            rows={4} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--bg)', color: 'var(--text)' }} />
                    ) : (q.options || []).map((opt, oi) => (
                        <label key={oi} style={{ display: 'block', padding: 6, cursor: 'pointer' }}>
                            <input type="radio" name={q.id} checked={answers[q.id] === oi} onChange={() => setAnswers(s => ({ ...s, [q.id]: oi }))} /> {opt}
                        </label>
                    ))}
                </div>
            ))}
            <button onClick={submit} disabled={busy} style={primaryBtn}>{busy ? 'Évaluation…' : 'Soumettre'}</button>
            {result && !result.error && (
                <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: result.passed ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)' }}>
                    <strong>{result.passed ? '✓ Réussi' : '✗ Non validé'}</strong> — {result.score_pct}%
                    {result.ai_feedback && <p style={{ marginTop: 8 }}>{result.ai_feedback}</p>}
                    {result.spectral_scores && <pre style={{ fontSize: 12 }}>{JSON.stringify(result.spectral_scores, null, 2)}</pre>}
                </div>
            )}
            {result?.error && <div style={{ marginTop: 12, color: '#ef4444' }}>⚠ {result.error}</div>}
        </div>
    );
}

function CertificateButton({ courseId }) {
    const [cert, setCert] = useState(null);
    const [busy, setBusy] = useState(false);
    const get = async () => {
        setBusy(true);
        try { const { certificate } = await lmsApi.issueCertificate(courseId); setCert(certificate); }
        catch (e) { alert(e.message); } finally { setBusy(false); }
    };
    return (
        <div style={{ marginTop: 20, padding: 14, borderRadius: 10, background: 'rgba(199,164,90,0.12)' }}>
            <Award size={16} color="#c7a45a" /> <strong>Cours complété !</strong>
            {cert ? (
                <div style={{ marginTop: 8, fontSize: 13 }}>
                    Certificat <code>{cert.certificate_uid}</code>
                    {cert.pdf_url && <a href={cert.pdf_url} target="_blank" rel="noreferrer" style={{ display: 'block', color: ACCENT, marginTop: 4 }}>Télécharger le PDF</a>}
                </div>
            ) : (
                <button onClick={get} disabled={busy} style={{ ...primaryBtn, marginTop: 8 }}>{busy ? '…' : 'Obtenir mon certificat'}</button>
            )}
        </div>
    );
}

const ProgressBar = ({ pct }) => (
    <div style={{ background: 'var(--card-border)', borderRadius: 6, height: 8, overflow: 'hidden', margin: '6px 0 2px' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? '#22c55e' : ACCENT, transition: 'width .3s' }} />
    </div>
);
const Centered = ({ children }) => <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: '100%', color: 'var(--text-secondary)' }}>{children}</div>;
const primaryBtn = { padding: '10px 16px', borderRadius: 8, background: ACCENT, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 };
const iconBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 7,
    background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--card-border)',
    cursor: 'pointer', fontWeight: 600, fontSize: 12, lineHeight: 1.2 };
const linkBtn = { background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontSize: 13, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 };
